import { OperationVariables, QueryHookOptions } from '@apollo/client';
import { AutocompleteInputChangeReason } from '@mui/material';
import { find, isEmpty, keyBy, uniqBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { CompactQueryData } from '@/components/form/baseInputs/BackendTypeaheadSelectInput/hooks/useAsyncTypeaheadProps';
import { getNodes } from '@/utils/graphqlUtils';

import {
  AsyncFilterGroup,
  AsyncFilterGroupQuery,
  FilterOption,
} from '../types';

// Define the return type for the hook
export interface UseAsyncFilterReturn {
  options: FilterOption[];
  loading: boolean;
  inputText: string;
  onInputChange: (
    newText: string,
    reason: AutocompleteInputChangeReason
  ) => void;
}

/**
 * Hook to manage fetching and state for asynchronous filter options within a FilterGroup.
 * Mirrors the logic of useAsyncTypeaheadProps but is specific to the TableFilter context.
 */
export function useAsyncFilterOptions<
  Query extends AsyncFilterGroupQuery,
  Variables extends OperationVariables = OperationVariables,
>(
  group: AsyncFilterGroup<Query, Variables>,
  selectedValues: string[]
): UseAsyncFilterReturn {
  const {
    useQueryHook,
    searchTermToVariables,
    getSelectedOptionVariables,
    toOptions,
    pageSize = 50, // Default page size
  } = group;

  const [inputText, setInputText] = useState('');
  // Track the last selected *valid* option to keep it in the list
  const [lastSelectedOption, setLastSelectedOption] =
    useState<FilterOption | null>(null);
  // Store all fetched options keyed by value for quick lookup
  const [allOptionsByValue, setAllOptionsByValue] = useState<
    Record<string, FilterOption>
  >({});
  // Store the most recent set of options returned by the query
  const [mostRecentOptions, setMostRecentOptions] = useState<FilterOption[]>(
    []
  );

  const queryArgs = useMemo<QueryHookOptions<Query, Variables>>(() => {
    const appliedSearchTerm = (() => {
      const queryIsExactValue = inputText === lastSelectedOption?.label;

      // Don't search if the input exactly matches the last selection or the empty option text
      if (queryIsExactValue) return '';
      return inputText;
    })();

    const variables: Variables = {
      // Base variables provided by the specific AsyncFilterGroup config
      ...(searchTermToVariables(appliedSearchTerm) as Variables),
      ...(getSelectedOptionVariables(selectedValues) as Variables),
      first: pageSize, // Add pagination
    };

    // Return the full options object for the query hook
    return {
      variables,
      fetchPolicy: 'cache-and-network', // Standard policy for typeaheads
      // Pass through any other options from the AsyncFilterGroup config if needed in future
    } as QueryHookOptions<Query, Variables>;
  }, [
    pageSize,
    inputText,
    lastSelectedOption?.label,
    searchTermToVariables,
    getSelectedOptionVariables,
    selectedValues,
  ]);

  // Execute the query
  const queryResult = useQueryHook(queryArgs);
  // Explicitly handle loading state for cache-and-network
  const { data, loading: loadingQuery } = queryResult;
  const loading = loadingQuery && !data;

  // Process the fetched data into FilterOption format
  const compactData = useMemo(() => {
    const searchResultData = getNodes(data?.queryData) as CompactQueryData<
      Query,
      Variables
    >[];
    const selectedOptionsData = getNodes(
      data?.selectedData
    ) as CompactQueryData<Query, Variables>[];

    if (!isEmpty(searchResultData) && Boolean(inputText)) {
      // If we're actively searching, only show the search results
      return searchResultData;
    }

    return [...selectedOptionsData, ...searchResultData];
  }, [data, inputText]);

  // Convert compacted data to FilterOptions using the provided mapper
  const queryOptions = useMemo(() => {
    return uniqBy(toOptions(compactData), (o) => o.value);
  }, [compactData, toOptions]);

  // Update state when new options are fetched
  useEffect(() => {
    const optionsByValue = keyBy(queryOptions, 'value');
    if (queryOptions.length > 0) {
      setMostRecentOptions(queryOptions);
    }
    // Merge new options into the master list
    setAllOptionsByValue((prev) => ({ ...prev, ...optionsByValue }));
  }, [queryOptions]);

  const handleInputChange = useCallback(
    (newSearchQuery: string, reason: AutocompleteInputChangeReason) => {
      setInputText(newSearchQuery); // Always update the raw input text

      // Find if the new input text matches a known option
      const matchingOption = find(
        allOptionsByValue,
        (opt) => opt.label === newSearchQuery
      );

      if (reason === 'input') {
        if (matchingOption) {
          // If typing matches a valid option, store it as potentially the 'last selected'
          setLastSelectedOption(matchingOption);
        }
        // If typing *doesn't* match, `lastSelectedOption` remains unchanged until a valid option is selected or cleared
      } else if (reason === 'reset') {
        if (matchingOption) {
          setLastSelectedOption(matchingOption);
        }
      } else if (reason === 'clear') {
        // Explicit clear action
        setLastSelectedOption(null);
        setInputText(''); // Ensure input text is cleared
      }
    },
    [allOptionsByValue]
  );

  // Combine options for display: empty + last selected + most recent results
  const finalOptions = useMemo(() => {
    if (loading) return mostRecentOptions;

    return queryOptions;
  }, [loading, mostRecentOptions, queryOptions]);

  // Return the state and handlers needed by the FilterGroup component
  return {
    options: finalOptions,
    loading,
    inputText,
    onInputChange: handleInputChange,
  };
}
